import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Icon,
  Link,
  Image,
  useColorModeValue,
  AspectRatio,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaReact,
  FaPhp,
  FaAndroid,
  FaPython,
  FaHtml5,
  FaCss3,
  FaJs,
  FaShoppingCart,
  FaStore,
  FaGlobe,
  FaTasks,
  FaRobot,
  FaShieldAlt,
  FaVoteYea,
  FaGraduationCap,
  FaExternalLinkAlt,
  FaGithub,
} from "react-icons/fa";
import { SiNextdotjs, SiFlask, SiMysql } from "react-icons/si";
import DotField from "../../components/UX/DotField";
import project1 from "../../assets/project-1.png";
import project2 from "../../assets/project-2.png";
import project3 from "../../assets/project-3.png";
import project4 from "../../assets/project-4.png";
import project5 from "../../assets/project-5.PNG";
import project6 from "../../assets/project-6.PNG";
import project7 from "../../assets/project-7.PNG";
import { ScrollToTop } from "../../components";

const projects = [
  {
    title: "DKD E-commerce Food Solution",
    description:
      "A comprehensive e-commerce platform for food ordering and delivery, built with modern web technologies for seamless user experience.",
    tech: ["Next.js", "TypeScript", "MongoDB"],
    icon: SiNextdotjs,
    projectIcon: FaShoppingCart,
    color: "green.400",
    gradientFrom: "#059669",
    gradientTo: "#047857",
    features: ["Online Ordering", "Real-time Tracking", "Payment Integration"],
    category: "Full Stack",
    image: project1,
    placeholderColor: "#065f46",
    liveLink: "https://dkdmarketing.vercel.app/",
  },
  {
    title: "SaleSavant",
    description:
      "Complete Point of Sale system with admin dashboard, kitchen display, and cashier interface for streamlined restaurant operations.",
    tech: ["React.js", "Node.js", "MongoDB"],
    icon: FaReact,
    projectIcon: FaStore,
    color: "blue.400",
    gradientFrom: "#2563eb",
    gradientTo: "#1d4ed8",
    features: [
      "Admin Panel",
      "Kitchen Display",
      "Cashier Interface",
      "Real-time Updates",
    ],
    category: "Full Stack",
    image: project2,
    placeholderColor: "#1e3a5f",
  },
  {
    title: "Solution Deck",
    description:
      "Modern landing page solution showcasing business services with stunning animations and responsive design.",
    tech: ["React.js", "Framer Motion", "CSS3"],
    icon: FaReact,
    projectIcon: FaGlobe,
    color: "cyan.400",
    gradientFrom: "#0891b2",
    gradientTo: "#0e7490",
    features: ["Responsive Design", "Animated UI", "SEO Optimized"],
    category: "Frontend",
    image: project3,
    placeholderColor: "#164e63",
    liveLink: "https://solution-deck.netlify.app/",
  },
  {
    title: "TrackEase",
    description:
      "Project Management System with monitoring capabilities for tracking progress, tasks, and team performance.",
    tech: ["Next.js", "TypeScript", "PostgreSQL"],
    icon: SiNextdotjs,
    projectIcon: FaTasks,
    color: "purple.400",
    gradientFrom: "#7c3aed",
    gradientTo: "#6d28d9",
    features: [
      "Task Management",
      "Team Collaboration",
      "Progress Tracking",
      "Analytics",
    ],
    category: "Full Stack",
    image: project4,
    placeholderColor: "#3b1f6e",
  },
  {
    title: "PATH: AI Resume Builder",
    description:
      "Intelligent resume builder powered by AI to help job seekers create professional, tailored resumes effortlessly.",
    tech: ["Next.js", "Gemini", "TailwindCSS"],
    icon: SiNextdotjs,
    projectIcon: FaRobot,
    color: "orange.400",
    gradientFrom: "#ea580c",
    gradientTo: "#c2410c",
    features: ["AI-Powered", "Custom Templates", "Export PDF", "ATS-Friendly"],
    category: "AI/ML",
    image: project5,
    placeholderColor: "#7c2d12",
    liveLink: "https://path-resumebuilder.vercel.app/",
  },
  {
    title: "MALDROID: APK Scanner",
    description:
      "Android APK analysis tool using AI to detect malware and security vulnerabilities in mobile applications.",
    tech: ["Android Studio", "Flask", "Python", "Machine Learning"],
    icon: FaAndroid,
    projectIcon: FaShieldAlt,
    color: "red.400",
    gradientFrom: "#dc2626",
    gradientTo: "#b91c1c",
    features: ["Malware Detection", "Security Analysis", "AI-Powered Scanning"],
    category: "AI/ML",
    image: project6,
    placeholderColor: "#7f1d1d",
    liveLink: "https://www.maldroidai.site/",
  },
  {
    title: "Voting Information Management System",
    description:
      "Web-based voting management system for handling elections, voter registration, and result tabulation.",
    tech: ["PHP", "MySQL", "JavaScript", "Bootstrap"],
    icon: FaPhp,
    projectIcon: FaVoteYea,
    color: "indigo.400",
    gradientFrom: "#4f46e5",
    gradientTo: "#4338ca",
    features: [
      "Voter Registration",
      "Ballot Management",
      "Result Tabulation",
      "Admin Dashboard",
    ],
    category: "Full Stack",
    image: "/projects/voting-system.jpg",
    placeholderColor: "#312e81",
  },
  {
    title: "How-to Survive STEM",
    description:
      "A helpful blog site providing tips, resources, and guides for students navigating STEM education.",
    tech: ["HTML5", "CSS3", "JavaScript"],
    icon: FaHtml5,
    projectIcon: FaGraduationCap,
    color: "pink.400",
    gradientFrom: "#db2777",
    gradientTo: "#be185d",
    features: ["Blog Posts", "Resource Library", "Study Tips", "Community"],
    category: "Frontend",
    image: project7,
    placeholderColor: "#831843",
    liveLink: "https://trebor-katalbas.github.io/HowToSurviveSTEM/",
  },
];

const techIconMap = {
  "React.js": FaReact,
  "Next.js": SiNextdotjs,
  React: FaReact,
  "Node.js": SiNextdotjs,
  PHP: FaPhp,
  Flask: SiFlask,
  Python: FaPython,
  MySQL: SiMysql,
  "Android Studio": FaAndroid,
  HTML5: FaHtml5,
  CSS3: FaCss3,
  JavaScript: FaJs,
};
// eslint-disable-next-line
const getTechIcon = (tech) => techIconMap[tech] || FaReact;

const ProjectImagePlaceholder = React.memo(({ project, isHovered }) => {
  const hasValidImage =
    project.image && !project.image.startsWith("/projects/");
  const isPlaceholderImage =
    project.image && project.image.startsWith("/projects/");

  return (
    <Box position="relative">
      <AspectRatio ratio={16 / 9} width="100%">
        <Box
          position="relative"
          overflow="hidden"
          borderTopRadius="lg"
          bg={project.placeholderColor}
          style={{ willChange: isHovered ? "transform" : "auto" }}
        >
          {!hasValidImage && (
            <Box
              position="absolute"
              inset={0}
              bgGradient={`linear(to-br, ${project.gradientFrom}80, ${project.gradientTo}80)`}
              zIndex={1}
            />
          )}

          {!hasValidImage && (
            <Icon
              as={project.projectIcon}
              position="absolute"
              width="50%"
              height="50%"
              color="whiteAlpha.300"
              zIndex={0}
              transform="auto"
              rotate={isHovered ? "-15deg" : "0deg"}
              scale={isHovered ? 1.2 : 1}
              transition="all 0.5s ease"
            />
          )}

          <HStack
            position="absolute"
            bottom={3}
            left={3}
            spacing={2}
            zIndex={2}
            flexWrap="wrap"
          >
            {project.tech.slice(0, 3).map((tech, idx) => (
              <Badge
                key={idx}
                colorScheme="whiteAlpha"
                bg="blackAlpha.600"
                backdropFilter="blur(4px)"
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="md"
              >
                {tech}
              </Badge>
            ))}
            {project.tech.length > 3 && (
              <Badge
                colorScheme="whiteAlpha"
                bg="blackAlpha.600"
                backdropFilter="blur(4px)"
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="md"
              >
                +{project.tech.length - 3}
              </Badge>
            )}
          </HStack>

          <Image
            src={project.image}
            alt={project.title}
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
            objectFit="cover"
            opacity={hasValidImage ? 1 : isPlaceholderImage ? 0.3 : 0}
            zIndex={0}
            fallback={<Box />}
            transition="all 0.5s ease"
            transform={isHovered ? "scale(1.1)" : "scale(1)"}
            loading="lazy"
            decoding="async"
          />

          <Box
            position="absolute"
            inset={0}
            border="2px solid"
            borderColor={isHovered ? `${project.color}80` : "transparent"}
            borderTopRadius="lg"
            transition="all 0.3s ease"
            zIndex={3}
          />
        </Box>
      </AspectRatio>

      {(project.liveLink || project.githubLink) && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={4}
          bg="blackAlpha.600"
          opacity={isHovered ? 1 : 0}
          transition="all 0.3s ease"
          zIndex={4}
          borderTopRadius="lg"
          pointerEvents={isHovered ? "auto" : "none"}
        >
          {project.liveLink && (
            <Tooltip label="Visit" placement="top">
              <Link
                href={project.liveLink}
                isExternal
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Box
                    bg="whiteAlpha.200"
                    backdropFilter="blur(10px)"
                    p={3}
                    borderRadius="full"
                    cursor="pointer"
                    _hover={{ bg: "whiteAlpha.400" }}
                    transition="all 0.2s ease"
                  >
                    <Icon as={FaExternalLinkAlt} w={5} h={5} color="white" />
                  </Box>
                </motion.div>
              </Link>
            </Tooltip>
          )}
          {project.githubLink && (
            <Tooltip label="View Source Code" placement="top">
              <Link
                href={project.githubLink}
                isExternal
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Box
                    bg="whiteAlpha.200"
                    backdropFilter="blur(10px)"
                    p={3}
                    borderRadius="full"
                    cursor="pointer"
                    _hover={{ bg: "whiteAlpha.400" }}
                    transition="all 0.2s ease"
                  >
                    <Icon as={FaGithub} w={5} h={5} color="white" />
                  </Box>
                </motion.div>
              </Link>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
});

const ProjectCard = React.memo(({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const bgColor = useColorModeValue("whiteAlpha.100", "whiteAlpha.100");
  const borderColor = useColorModeValue("whiteAlpha.200", "whiteAlpha.200");
  const hasLinks = project.liveLink || project.githubLink;

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ height: "100%" }}
    >
      <Box
        bg={bgColor}
        borderRadius="xl"
        border="1px solid"
        borderColor={isHovered ? project.color : borderColor}
        backdropFilter="blur(10px)"
        transition="all 0.3s ease"
        position="relative"
        overflow="hidden"
        height="100%"
        _hover={{
          transform: "translateY(-8px)",
          boxShadow: `0 20px 40px ${project.placeholderColor}40`,
        }}
        style={{ willChange: "transform" }}
      >
        <ProjectImagePlaceholder project={project} isHovered={isHovered} />

        <VStack
          spacing={4}
          align="stretch"
          p={6}
          position="relative"
          zIndex={1}
        >
          <HStack justify="space-between">
            <Badge
              colorScheme="purple"
              variant="subtle"
              fontSize="xs"
              px={2}
              py={1}
              borderRadius="full"
            >
              {project.category}
            </Badge>
            <Icon
              as={project.icon}
              w={6}
              h={6}
              color={project.color}
              transition="transform 0.3s ease"
              transform={isHovered ? "scale(1.2) rotate(10deg)" : "scale(1)"}
            />
          </HStack>

          <Heading as="h3" size="md" color="white" transition="color 0.3s ease">
            {project.title}
          </Heading>

          <Text color="gray.400" fontSize="sm" lineHeight="1.6" noOfLines={3}>
            {project.description}
          </Text>

          <Box>
            <HStack flexWrap="wrap" spacing={2}>
              {project.features.slice(0, 3).map((feature, idx) => (
                <Badge
                  key={idx}
                  colorScheme="whiteAlpha"
                  variant="subtle"
                  fontSize="xs"
                >
                  {feature}
                </Badge>
              ))}
              {project.features.length > 3 && (
                <Badge colorScheme="whiteAlpha" variant="subtle" fontSize="xs">
                  +{project.features.length - 3}
                </Badge>
              )}
            </HStack>
          </Box>

          {hasLinks && (
            <HStack
              spacing={4}
              pt={2}
              opacity={isHovered ? 1 : 0.7}
              transition="all 0.3s ease"
            >
              {project.liveLink && (
                <Link
                  href={project.liveLink}
                  isExternal
                  _hover={{ textDecoration: "none" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <HStack spacing={1} color={project.color}>
                    <Text fontSize="sm" fontWeight="medium">
                      Go to
                    </Text>
                    <motion.div
                      animate={{ x: isHovered ? 3 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Icon as={FaExternalLinkAlt} w={3} h={3} />
                    </motion.div>
                  </HStack>
                </Link>
              )}
              {project.githubLink && (
                <Link
                  href={project.githubLink}
                  isExternal
                  _hover={{ textDecoration: "none" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <HStack spacing={1} color={project.color}>
                    <Text fontSize="sm" fontWeight="medium">
                      Source Code
                    </Text>
                    <motion.div
                      animate={{ x: isHovered ? 3 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Icon as={FaGithub} w={3} h={3} />
                    </motion.div>
                  </HStack>
                </Link>
              )}
            </HStack>
          )}
        </VStack>
      </Box>
    </motion.div>
  );
});

const CTASection = React.memo(({ animationDelay }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 1,
        delay: animationDelay,
        ease: "easeInOut",
      }}
      className="w-full"
    >
      <Box
        position="relative"
        p={{ base: 6, sm: 10 }}
        bg="rgba(255, 255, 255, 0.05)"
        borderRadius="lg"
        border="2px"
        borderColor="#ffffff1a"
        textAlign="center"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ willChange: "contents" }}
      >
        {isHovered && (
          <>
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              bg={`radial-gradient(400px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255, 255, 255, 0.02), transparent 40%)`}
              zIndex="1"
              borderRadius="inherit"
              pointerEvents="none"
            />
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              bg={`radial-gradient(200px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255, 255, 255, 0.09), transparent 40%)`}
              zIndex="1"
              borderRadius="inherit"
              pointerEvents="none"
            />
          </>
        )}
        <VStack spacing={4}>
          <h2 className="text-txt text-xl sm:text-2xl lg:text-4xl font-normal">
            Have a project in mind?
          </h2>
          <p className="text-zinc-500 text-sm sm:text-xl lg:text-2xl font-light text-center pb-5">
            Let's collaborate and bring your ideas to life
          </p>
          <Link href="/#contact">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Badge
                px={6}
                py={3}
                borderRadius="full"
                fontSize="md"
                cursor="pointer"
                className="bg-[#6778cb]"
                variant="solid"
                transition="all 0.3s ease"
              >
                Get in Touch
              </Badge>
            </motion.div>
          </Link>
        </VStack>
      </Box>
    </motion.div>
  );
});

const Projects = () => {
  const [showHero, setShowHero] = useState(true);
  const [heroRemoved, setHeroRemoved] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");

  const animationDelay = 0.5 * 0.5;
  const categories = useMemo(
    () => ["All", "Full Stack", "Frontend", "AI/ML"],
    [],
  );

  const filteredProjects = useMemo(
    () =>
      activeFilter === "All"
        ? projects
        : projects.filter((p) => p.category === activeFilter),
    [activeFilter],
  );

  useEffect(() => {
    window.scrollTo(0, -5000);
  }, []);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      lastScrollY = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (lastScrollY > 100) {
            setShowHero(false);
            setTimeout(() => {
              setHeroRemoved(true);
            }, 300);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <DotField
        dotRadius={4}
        dotSpacing={30}
        cursorRadius={500}
        cursorForce={0.05}
        bulgeStrength={67}
        bulgeOnly={false}
        glowRadius={60}
        gradientFrom="#2e2e2e"
        gradientTo="#23133f"
        glowColor="#0b0b0b"
      />

      <AnimatePresence>
        {!showHero && (
          <ScrollToTop />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showHero && (
          <motion.div
            key="hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "transparent",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <VStack spacing={4}>
                <Heading
                  fontSize={{ base: "4xl", md: "7xl" }}
                  fontWeight="bold"
                  color="#6778cb"
                  textAlign="center"
                >
                  Projects
                </Heading>
                <Text color="gray.400" fontSize="xl" textAlign="center">
                  Scroll down to explore all my works
                </Text>
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Text color="gray.500" fontSize="4xl">
                    ↓
                  </Text>
                </motion.div>
              </VStack>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Projects Content */}
      <Box
        className="main-content"
        position="relative"
        zIndex={20}
        style={{
          paddingTop: heroRemoved ? "0px" : "100vh",
          transition: "padding-top 0.3s ease-in-out",
        }}
      >
        <Container maxW="1200px" py={20}>
          <VStack spacing={16}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <VStack spacing={4} textAlign="center">
                <h2 className="text-txt text-xl sm:text-3xl lg:text-5xl font-normal">
                  Featured Work
                </h2>
                <p className="text-zinc-500 text-sm sm:text-xl lg:text-2xl font-light text-center">
                  A collection of projects showcasing expertise in full-stack
                  development, AI/ML, and mobile applications
                </p>
              </VStack>
            </motion.div>

            <HStack spacing={4} wrap="wrap" justify="center">
              {categories.map((category) => (
                <motion.div
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Badge
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontSize="sm"
                    cursor="pointer"
                    colorScheme={
                      activeFilter === category ? "green" : "whiteAlpha"
                    }
                    variant={activeFilter === category ? "solid" : "subtle"}
                    onClick={() => setActiveFilter(category)}
                    transition="all 0.3s ease"
                  >
                    {category}
                  </Badge>
                </motion.div>
              ))}
            </HStack>

            <motion.div layout style={{ width: "100%" }}>
              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={8}
                width="100%"
              >
                <AnimatePresence mode="wait">
                  {filteredProjects.map((project, index) => (
                    <motion.div
                      key={project.title}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ProjectCard project={project} index={index} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </SimpleGrid>
            </motion.div>

            <CTASection animationDelay={animationDelay} />
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default React.memo(Projects);
